import { Injectable, Signal, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Auth, authState } from '@angular/fire/auth';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { VenueDataService } from './venue-data.service';
import { Venue } from '../models/venue';

/** Extracts the venue id from a `/venues/:id/...` URL, or null when not on a venue route. */
function venueIdFromUrl(url: string): string | null {
  const match = url.match(/^\/venues\/([^/;?#]+)/);
  return match ? match[1] : null;
}

/**
 * Single source of truth for the currently routed venue, for chrome that
 * lives outside the routed page (sidebar, headers).
 *
 * The venue id is derived from the router URL, so no component has to push
 * it. The venue stream is auth-gated (never reads before sign-in resolves,
 * drops the listener on sign-out) and goes through VenueDataService's
 * shared streams, so the chrome and the routed page share ONE Firestore
 * listener per document instead of stacking their own. `switchMap` tears
 * the old listener down before opening the next when the venue changes.
 */
@Injectable({ providedIn: 'root' })
export class VenueContextService {
  private venueData = inject(VenueDataService);
  private auth = inject(Auth);
  private router = inject(Router);

  private venueIdSignal = signal<string | null>(venueIdFromUrl(this.router.url));
  /** Id of the venue in the current URL, or null when not on a venue route. */
  readonly venueId: Signal<string | null> = this.venueIdSignal.asReadonly();

  /** The one shared live stream of the routed venue document. */
  readonly venue$: Observable<Venue | null> = combineLatest([
    toObservable(this.venueIdSignal),
    authState(this.auth),
  ]).pipe(
    switchMap(([venueId, user]) => venueId && user
      ? this.venueData.getVenue(venueId).pipe(
          map(venue => venue ?? null),
          catchError(error => {
            console.error('VenueContext: failed to load venue', venueId, error);
            return of(null);
          })
        )
      : of(null)
    ),
    shareReplay({ bufferSize: 1, refCount: false })
  );
  /** Latest venue document, or null while loading / with no venue routed. */
  readonly venue: Signal<Venue | null> = toSignal(this.venue$, { initialValue: null });

  constructor() {
    // Root service, lives for the app lifetime — no teardown needed.
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.venueIdSignal.set(venueIdFromUrl(this.router.url)));
  }
}
